import {
  EPDGroupGraphic,
  EPDGroupInsightsIN,
  PrefillTooltip,
  ProductCreationEPDGroup,
  PropertyTypeChoices,
  ReferenceUnitChoices,
} from "./../../api-client/generated/api";
import { AxiosResponse } from "axios";
import {
  CompliantCertification,
  CPXValueOUT,
  CustomBuildingProductIN,
  CustomBuildingProductOUT,
  CustomBuildingProductReferenceUnitEnum,
  EPDGroupsCheckOUT,
  EPDOUT,
  EPDSearchIN,
  EPDSearchOUT,
  EPDSearchSortBy,
  FileUploadSchema,
  MaterialSearchOUT,
  MaterialSearchSortBy,
  ProductHealthCheckedStatusEnum,
  SimpleMaterialOut,
  SortOrder,
} from "../../api-client/generated/api";
import { action, makeAutoObservable, runInAction } from "mobx";
import {
  buildingProductEditorApi,
  epdInsightsApi,
  fileUploadApi,
  getLocalAuthHeader,
  inventoryEPDsApi,
  inventoryProductsApi,
  MaterialSearchIN,
  productEditorApi,
  searchAPI,
} from "api-client";
import { BGItem } from "components/ButtonGroups";
import { REFERENCE_UNITS } from "features/MappingEditor/consts";
import { buildingStore } from "store/Building/BuildingStore";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";
import { searchStore } from "features/ProductSearch/SearchStore";
import { PatternDictionary } from "./components/GraphicObject";
import { all_svgs } from "./features/svgs";

export interface Option {
  id: string | number;
  name: string;
  disabled?: boolean;
  isSelected?: boolean;
}
interface Tag {
  id: string;
  name: string;
}

const rawEPDItem = {
  factor: undefined,
  epd: {
    id: "",
    source: "",
    source_uuid: "",
    version: "",
    reference_size: 0,
    reference_unit: "",
    material: "",
    is_sentinel: false,
  },
  material: undefined,
  percentage: undefined,
};

export const rawEPDGroup: EPDGroup = {
  epds: [rawEPDItem],
  conformity: "",
  certifications: [],
  is_health_checked: false,
  health_checked: undefined,
  generate_graphic: false,
};

interface EPD extends EPDSearchOUT {
  material?: string;
}
export interface EPDRowProps {
  factor?: number | string;
  epd: EPD | undefined;
  material: MaterialSearchOUT | undefined;
  percentage: number | undefined | null;
}
export interface EPDGroup {
  epds: EPDRowProps[];
  conformity: string | undefined | null;
  certifications: CompliantCertification[] | undefined;
  is_health_checked: boolean | undefined;
  health_checked: ProductHealthCheckedStatusEnum | undefined | null;
  generate_graphic: boolean;
}

interface FilterOptions {
  epd_type: Option[];
  epd_data_sources: Option[];
  material_categories: SimpleMaterialOut[];
}

interface Image {
  id: string;
  image: string;
}

interface FileProps {
  file_id: string;
  file_name: string;
  url: string;
  created: string;
}

export type PropertyValue =
  | string
  | number
  | boolean
  | string[]
  | undefined
  | null;

export interface Property {
  id?: string;
  slug: string;
  property_type: PropertyTypeChoices;
  name: string;
  value: PropertyValue;
  options?: string[];
}

export interface CustomProduct {
  id: string;
  name: string;
  groups: EPDGroup[];
  din_category: string;
  life_span: undefined | number;
  auto_mapping_tags: Tag[];
  preuse_qualification?: string | number;
  separability_score: undefined | number;
  separability_qualification: undefined | string | number;
  deconstructability_score: undefined | number;
  deconstructability_qualification: undefined | string | number;
  reusability_score: undefined | number;
  reusability_qualification: undefined | string | number;
  recyclability_score: undefined | number;
  recyclability_qualification: undefined | string | null;
  dataset_source: undefined | string | null;
  reference_unit?: BGItem;
  images: Image[];
  files: FileProps[];
  diagrams: Image[];
  properties: Property[];
  is_part_of_a_component?: boolean;
}

type EPDGroupValue =
  | MaterialSearchOUT
  | string
  | number
  | EPDSearchOUT
  | undefined;

const initialData: CustomProduct = {
  id: "",
  name: "",
  groups: [rawEPDGroup],
  din_category: "",
  life_span: undefined,
  auto_mapping_tags: [],
  preuse_qualification: "",
  separability_score: undefined,
  separability_qualification: "",
  deconstructability_score: undefined,
  deconstructability_qualification: "",
  reusability_score: undefined,
  reusability_qualification: "",
  recyclability_score: undefined,
  recyclability_qualification: "",
  dataset_source: "",
  reference_unit: REFERENCE_UNITS[0],
  images: [],
  files: [],
  diagrams: [],
  properties: [],
};

const tooltipsStatus = {
  name: undefined,
  life_span: undefined,
  preuse_qualification: undefined,
  separability_qualification: undefined,
  deconstructability_qualification: undefined,
  reusability_qualification: undefined,
};

interface Materials {
  count: number;
  items: MaterialSearchOUT[];
  loading: boolean;
}

interface EPDs {
  count: number;
  items: EPDSearchOUT[];
  loading: boolean;
}

export interface OptionsList {
  [key: string]: (CPXValueOUT | Option)[];
}

type ShowTooltips = {
  [key: string]: PrefillTooltip | undefined | null;
};

class BuildingProductStore {
  generalInformation = {
    title: "generalInformation",
    fields: ["name", "din_category", "life_span", "auto_mapping_tags"],
  };
  circularity = {
    title: "circularity",
    fields: [
      "preuse_qualification",
      "separability_qualification",
      "deconstructability_qualification",
      "reusability_qualification",
    ],
  };
  epdData = {
    title: "referenceUnitAndEnvironmentalData",
    fields: ["groups"],
  };
  epdsValidationErrors: { groupIndex: number; rowIndex: number }[] = [];
  factorValidationErrors: { groupIndex: number; rowIndex: number }[] = [];
  materialValidationErrors: { groupIndex: number; rowIndex: number }[] = [];
  saveValidationErrors = true;
  data: CustomProduct = initialData;
  optionsLists: OptionsList = {
    din_category: [],
    preuse_qualification: [],
    separability_qualification: [],
    deconstructability_qualification: [],
    reusability_qualification: [],
  };
  materials: Materials = {
    count: 0,
    items: [],
    loading: false,
  };
  epds: EPDs = { count: 0, items: [], loading: false };
  filtersOptions: FilterOptions = {
    epd_type: [],
    epd_data_sources: [],
    material_categories: [],
  };
  productSpecifications: EPDGroupsCheckOUT | undefined;
  imagesToRemove: string[] = [];
  filesToRemove: string[] = [];
  uploadedImagesIDs: string[] = [];
  uploadedFilesIDs: string[] = [];
  uploadedDiagramIDs: string[] = [];
  imagesToUpload: File[] = [];
  filesToUpload: File[] = [];
  diagramsToUpload: File[] = [];
  graphicData: EPDGroupGraphic | null = null;
  insightsLoading = false;
  patternImages: PatternDictionary = {};
  showTooltips: ShowTooltips = tooltipsStatus;

  resetData() {
    this.data = initialData;
    this.saveValidationErrors = true;
    this.uploadedImagesIDs = [];
    this.uploadedFilesIDs = [];
    this.uploadedDiagramIDs = [];
    this.imagesToRemove = [];
    this.filesToRemove = [];
    this.imagesToUpload = [];
    this.filesToUpload = [];
    this.diagramsToUpload = [];
    this.productSpecifications = undefined;
    this.epdsValidationErrors = [];
    this.factorValidationErrors = [];
    this.materialValidationErrors = [];
    this.graphicData = null;
    this.showTooltips = tooltipsStatus;
  }

  setData(data: CustomProduct) {
    this.data = data;
  }

  setOptionsLists(optionsLists: OptionsList) {
    this.optionsLists = optionsLists;
  }

  setProductSpecifications(productSpecifications: EPDGroupsCheckOUT) {
    this.productSpecifications = productSpecifications;
  }

  setSaveValidationErrors(saveValidationErrors: boolean) {
    this.saveValidationErrors = saveValidationErrors;
  }

  setShowTooltip(showTooltips: ShowTooltips) {
    this.showTooltips = showTooltips;
  }

  onChangeData(
    name: string,
    value: string | boolean | [] | undefined | Tag[] | BGItem
  ) {
    runInAction(() => {
      if (name) this.data[name as keyof CustomProduct] = value as never;
      if (this.showTooltips[name]) {
        this.showTooltips[name] = undefined;
      }
    });
    this.checkSaveValidity();
  }

  onChangeProperties(value: Property[]) {
    runInAction(() => {
      this.data.properties = value as [];
    });
  }

  updatePropertyValues(
    index: number,
    key: keyof Property,
    value: PropertyValue
  ) {
    const propertiesList = [...this.data.properties];
    runInAction(() => {
      if (key === "slug") {
        propertiesList[index].slug = value as string;
        propertiesList[index].id = value as string;
      } else if (key === "value") {
        propertiesList[index].value = value;
      } else if (key === "name") {
        propertiesList[index].name = value as string;
      }
    });
    this.onChangeProperties(propertiesList);
  }

  onChangeImages(images: { id: string; image: string }[]) {
    runInAction(() => {
      this.data.images = images;
    });
  }

  setImagesToUpload(imagesToUpload: File[]) {
    this.imagesToUpload = imagesToUpload;
  }

  setDiagramsToUpload(diagramsToUpload: File[]) {
    this.diagramsToUpload = diagramsToUpload;
  }

  setImagesToRemove(imagesToRemove: string[]) {
    this.imagesToRemove = imagesToRemove;
  }

  setUploadedImagesIDs(uploadedImagesIDs: string[]) {
    this.uploadedImagesIDs = uploadedImagesIDs;
  }

  onChangeFiles(files: FileProps[]) {
    this.data.files = files;
  }

  setFilesToUpload(filesToUpload: File[]) {
    this.filesToUpload = filesToUpload;
  }

  setFilesToRemove(filesToRemove: string[]) {
    this.filesToRemove = filesToRemove;
  }

  setUploadedFilesIDs(uploadedFilesIDs: string[]) {
    this.uploadedFilesIDs = uploadedFilesIDs;
  }

  setUploadedDiagramIDs(uploadedDiagramIDs: string[]) {
    this.uploadedDiagramIDs = uploadedDiagramIDs;
  }

  setGraphicData(epdGroupInsights: EPDGroupGraphic | null) {
    this.graphicData = epdGroupInsights;
  }

  setInsightsLoading(insightsLoading: boolean) {
    this.insightsLoading = insightsLoading;
  }

  setPatternImages(patternImages: PatternDictionary) {
    this.patternImages = patternImages;
  }

  onChangeEPDGroup(
    name: string,
    value: EPDGroupValue,
    groupIndex: number,
    rowIndex: number
  ) {
    if (name === "epd") {
      this.changeEpdValue(value, groupIndex, rowIndex);
    } else if (name === "material") {
      this.data.groups[groupIndex].epds[rowIndex].material =
        value as MaterialSearchOUT;
    } else {
      this.changeFactorValue(value, groupIndex, rowIndex);
    }
    this.setData({ ...this.data });
    this.checkSaveValidity();
    this.getEPDGroupInsights(groupIndex);
  }

  async onChangeGenerateGraphics(groupIndex: number, value: boolean) {
    if (!value) {
      this.data.groups[groupIndex].generate_graphic &&
        this.setGraphicData(null);
    }
    this.data.groups[groupIndex].generate_graphic = value;
    this.data.groups.forEach((item, index) => {
      if (index === groupIndex) {
        item.generate_graphic = value;
      } else {
        item.generate_graphic = false;
      }
    });
    this.setData({ ...this.data });
    if (value) {
      await this.getEPDGroupInsights(groupIndex);
    }
  }

  private changeFactorValue(
    value: string | number | MaterialSearchOUT | EPDSearchOUT | undefined,
    groupIndex: number,
    rowIndex: number
  ) {
    const _result = this.factorValidity(value as string);
    this.data.groups[groupIndex].epds[rowIndex].factor = _result as string;
    // clear this value while we wait for the true percentage from the backend
    this.data.groups[groupIndex].epds[rowIndex].percentage = undefined;
  }

  private async changeEpdValue(
    value: string | number | EPDSearchOUT | MaterialSearchOUT | undefined,
    groupIndex: number,
    rowIndex: number
  ) {
    const currentRow = this.data.groups?.[groupIndex]?.epds[rowIndex];
    if (!currentRow) return;
    const epd_value: EPDSearchOUT = value as EPDSearchOUT;
    currentRow.epd = epd_value;
    if (epd_value !== undefined) {
      if (epd_value.default_material) {
        currentRow.material = epd_value.default_material as MaterialSearchOUT;
      }
      await this.checkEPDSpecifications(groupIndex);
    }
  }

  async checkEPDSpecifications(groupIndex: number) {
    const epdGroup = this.data.groups[groupIndex].epds
      .map((item) => item?.epd?.id)
      .flat(1)
      .filter((item) => item);
    await this.fetchEPDSpecifications(epdGroup.filter(Boolean) as string[])
      .then((response) => {
        if (!response) return;
        runInAction(() => {
          this.data.groups[groupIndex].conformity = response.conformity;
          this.data.groups[groupIndex].certifications =
            response.compliant_certifications?.filter(
              (item) => item.name !== "Keine"
            );
          this.data.groups[groupIndex].health_checked =
            response.health_checked_status;
        });
      })
      .catch((err) =>
        console.error(err, "ProductEditorCheckEpdSpecifications")
      );
    await this.getProductEPDSpecifications();
  }

  async fetchEPDSpecifications(epdIDs: string[]) {
    return await productEditorApi()
      .inventoryApiV1RoutersProductEditorCheckEpdSpecifications({
        epds: epdIDs as string[],
      })
      .then((response) => response.data)
      .catch((err) => console.error(err, "fetchEPDSpecifications"));
  }

  async getProductEPDSpecifications() {
    const epdGroupsIDs = this.data.groups
      .map((item) => item.epds.map((item) => item.epd?.id).filter(Boolean))
      .filter(Boolean) as string[][];

    return await productEditorApi()
      .inventoryApiV1RoutersProductEditorCheckEpdGroups({
        epd_groups: epdGroupsIDs.filter((item) => item.length > 0),
      })
      .then((response) => {
        const data = response.data;
        data &&
          this.setProductSpecifications({
            conformities: data.conformities,
            compliant_certifications: data.compliant_certifications?.filter(
              (item) => item !== "Keine"
            ),
            health_checked_status: data.health_checked_status,
          });
      })
      .catch((err) => console.error(err, "getProductEPDSpecifications"));
  }

  private factorValidity(
    value: string | number | EPDOUT | MaterialSearchOUT | undefined
  ) {
    return Number(value) < 0 ? undefined : value;
  }

  isAnItemOfOptions(key: string) {
    return !![
      "din_category",
      "preuse_qualification",
      "separability_qualification",
      "deconstructability_qualification",
      "reusability_qualification",
    ].find((item) => item === key);
  }

  getValueFromObject(key: string, isEng?: boolean) {
    const value = Object.entries(this.data).find(
      (item) => item[0] === key
    )?.[1];
    if (key === "auto_mapping_tags") {
      return value.map((item: Tag) => item.name).join(", ");
    } else if (key === "reference_unit") {
      return isEng ? value?.name : value?.name_de;
    } else if (this.isAnItemOfOptions(key)) {
      return this.optionsLists[key]?.find((item) => item.id === value)?.name;
    } else {
      return value;
    }
  }

  checkSaveValidation() {
    const info =
      this.data.name && this.data.din_category && this.data.life_span;
    const circularity =
      this.data.preuse_qualification &&
      this.data.separability_qualification &&
      this.data.deconstructability_qualification &&
      this.data.reusability_qualification;

    const epdGroups = this.data?.groups?.map((epdGroup: EPDGroup) =>
      epdGroup?.epds?.map((item) => item?.factor && item?.epd)
    );
    return info && circularity && epdGroups;
  }

  addEPDGroup() {
    this.data.groups?.push(rawEPDGroup);
    this.checkSaveValidity();
  }

  removeEPDGroup(groupIndex: number) {
    this.data.groups[groupIndex].generate_graphic && this.setGraphicData(null);
    this.updateValidations(groupIndex, 0);
    this.data.groups?.splice(groupIndex, 1);
    this.checkSaveValidity();
    this.getProductEPDSpecifications();
  }

  clearEPDGroup() {
    this.data.groups = [rawEPDGroup];
    this.updateValidations(0, 0);
    this.getProductEPDSpecifications();
    this.setGraphicData(null);
  }

  addEpdRow(index: number) {
    this.data.groups?.[index].epds?.push(rawEPDItem);
    // wait for the backend to return percentages
    this.data.groups?.[index].epds.forEach((e) => (e.percentage = undefined));
    this.checkSaveValidity();
  }

  removeEpdRow(rowIndex: number, groupIndex: number) {
    this.data.groups?.[groupIndex]?.epds?.splice(rowIndex, 1);
    this.updateValidations(groupIndex, rowIndex);
    this.checkEPDSpecifications(groupIndex);
    this.getProductEPDSpecifications();
    this.getEPDGroupInsights(groupIndex);
  }

  private updateValidations(groupIndex: number, rowIndex: number) {
    this.removeEpdsErrors(groupIndex, rowIndex);
    this.removeFactorErrors(groupIndex, rowIndex);
    this.removeMaterialErrors(groupIndex, rowIndex);
    this.checkSaveValidity();
  }

  checkEpdValidationErrors = () => {
    this.data?.groups?.forEach((group, groupIndex) =>
      group?.epds?.find((row, rowIndex) => {
        if (!row.epd?.id) {
          const itemExist = this.epdsValidationErrors.find(
            (item) =>
              item.groupIndex === groupIndex && item.rowIndex === rowIndex
          );
          if (!itemExist)
            this.epdsValidationErrors.push({ groupIndex, rowIndex });
        }
      })
    );
    return this.epdsValidationErrors.length > 0;
  };

  checkFactorValidationErrors = () => {
    this.data?.groups?.forEach((group, groupIndex) =>
      group?.epds?.find((row, rowIndex) => {
        if (!row.factor) {
          const itemExist = this.factorValidationErrors.find(
            (item) =>
              item.groupIndex === groupIndex && item.rowIndex === rowIndex
          );
          if (!itemExist)
            this.factorValidationErrors.push({ groupIndex, rowIndex });
        }
      })
    );
    return this.factorValidationErrors.length > 0;
  };

  addFactorValidationWarnings = () => {
    this.data?.groups?.forEach((group, groupIndex) =>
      group?.epds?.find((row, rowIndex) => {
        if (!row.factor) {
          const itemExist = this.factorValidationErrors.find(
            (item) =>
              item.groupIndex === groupIndex && item.rowIndex === rowIndex
          );
          if (!itemExist)
            this.factorValidationErrors.push({ groupIndex, rowIndex });
        }
      })
    );
    return this.factorValidationErrors.length > 0;
  };

  checkMaterialValidationErrors = () => {
    this.data?.groups?.forEach((group, groupIndex) =>
      group?.epds?.find((row, rowIndex) => {
        if (!row.material) {
          const itemExist = this.materialValidationErrors.find(
            (item) =>
              item.groupIndex === groupIndex && item.rowIndex === rowIndex
          );
          if (!itemExist)
            this.materialValidationErrors.push({ groupIndex, rowIndex });
        }
      })
    );
    return this.materialValidationErrors.length > 0;
  };

  removeEpdsErrors(groupIndex: number, rowIndex: number) {
    const currentItem = this.epdsValidationErrors.find(
      (item) => item.groupIndex === groupIndex && item.rowIndex === rowIndex
    );
    if (currentItem) {
      this.epdsValidationErrors.splice(
        this.epdsValidationErrors.indexOf(currentItem),
        1
      );
    }
  }

  removeFactorErrors(groupIndex: number, rowIndex: number) {
    const currentItem = this.factorValidationErrors.find(
      (item) => item.groupIndex === groupIndex && item.rowIndex === rowIndex
    );
    if (currentItem) {
      this.factorValidationErrors.splice(
        this.factorValidationErrors.indexOf(currentItem),
        1
      );
    }
  }

  removeMaterialErrors(groupIndex: number, rowIndex: number) {
    const currentItem = this.materialValidationErrors.find(
      (item) => item.groupIndex === groupIndex && item.rowIndex === rowIndex
    );
    if (currentItem) {
      this.materialValidationErrors.splice(
        this.materialValidationErrors.indexOf(currentItem),
        1
      );
    }
  }

  hasFactorValidationError(groupIndex: number, rowIndex?: number | undefined) {
    return Boolean(
      this.factorValidationErrors.find((item) =>
        rowIndex || rowIndex === 0
          ? item.groupIndex === groupIndex && item.rowIndex === rowIndex
          : item.groupIndex === groupIndex
      )
    );
  }

  hasEpdValidationError(groupIndex: number, rowIndex?: number) {
    return Boolean(
      this.epdsValidationErrors.find((item) =>
        rowIndex || rowIndex === 0
          ? item.groupIndex === groupIndex && item.rowIndex === rowIndex
          : item.groupIndex === groupIndex
      )
    );
  }

  hasMaterialValidationError(groupIndex: number, rowIndex?: number) {
    return Boolean(
      this.materialValidationErrors.find((item) =>
        rowIndex || rowIndex === 0
          ? item.groupIndex === groupIndex && item.rowIndex === rowIndex
          : item.groupIndex === groupIndex
      )
    );
  }

  anyFactorAdded() {
    return (
      this.data.groups.filter((item) => item.epds.find((item) => item.factor))
        .length > 0
    );
  }

  resetFactors() {
    this.data.groups.forEach((item) =>
      item.epds.forEach((item) => (item.factor = undefined))
    );
    this.saveValidationErrors = true;
    this.setData({ ...this.data });
  }

  checkSaveValidity() {
    const info =
      this.data.name != "" &&
      this.data.din_category &&
      this.data.life_span != undefined &&
      String(this.data.life_span) != "";
    const circularity =
      this.data.preuse_qualification != "" &&
      this.data.separability_qualification != "" &&
      this.data.deconstructability_qualification != "" &&
      this.data.reusability_qualification != "";
    let emptyFieldRow = undefined;
    this.data.groups?.forEach((group) => {
      group.epds?.forEach((row) => {
        if (!row.factor || !row.epd || !row.material) {
          emptyFieldRow = row;
        }
      });
    });
    this.saveValidationErrors =
      Boolean(emptyFieldRow != undefined) || !info || !circularity;
  }

  setMaterials(materials: Materials) {
    this.materials = materials;
  }

  resetMaterials() {
    this.materials = {
      count: 0,
      items: [],
      loading: false,
    };
  }

  setEPDs(epds: EPDs) {
    this.epds = epds;
  }

  resetEPDs() {
    this.epds = {
      count: 0,
      items: [],
      loading: false,
    };
  }

  onSelectEPDRow(item: EPDSearchOUT, groupIndex: number, rowIndex: number) {
    this.removeEpdsErrors(groupIndex, rowIndex);
    this.removeMaterialErrors(groupIndex, rowIndex);
    this.onChangeEPDGroup("epd", item, groupIndex, rowIndex);
    if (this.data.name === "") {
      // replace name with epd name if name is empty
      this.setData({ ...this.data, name: String(item.name_de) });
      this.showTooltips.name = PrefillTooltip.FromEpd;
    }
  }

  async fetchMaterial(
    pageNumber: number,
    pageSize: number,
    term?: string,
    category?: string[],
    sortOrder?: SortOrder | null,
    sortBy?: MaterialSearchSortBy | null
  ) {
    const props: MaterialSearchIN = { term: term, category_general: category };
    return await productEditorApi()
      .inventoryApiV1RoutersProductEditorSearchMaterials(
        props,
        sortOrder,
        sortBy,
        pageNumber,
        pageSize
      )
      .then((response: AxiosResponse) => response.data)
      .catch((err) => console.error(err, "ProductEditorSearchMaterials"));
  }

  async fetchEPDs(
    pageNumber: number,
    pageSize: number,
    props: EPDSearchIN,
    sortOrder: SortOrder | null,
    sortBy: EPDSearchSortBy | null
  ) {
    return await productEditorApi()
      .inventoryApiV1RoutersProductEditorSearchEpds(
        props,
        sortOrder,
        sortBy,
        pageNumber,
        pageSize
      )
      .then((response: AxiosResponse) => response.data)
      .catch((err) => console.error(err, "ProductEditorSearchEpds"));
  }

  async fetchEPDtypes() {
    await productEditorApi()
      .inventoryApiV1RoutersProductEditorGetEpdTypes()
      .then((response: AxiosResponse) => {
        const items = response.data.map((item: string, index: number) => ({
          id: index,
          name: item,
        }));
        this.setFiltersOptions({
          ...this.filtersOptions,
          epd_type: items,
        });
      })
      .catch((err) => console.error(err, "ProductEditorGetEpdTypes"));
  }

  async fetchEPDDatabases() {
    await productEditorApi()
      .inventoryApiV1RoutersProductEditorGetEpdDatabases()
      .then((response: AxiosResponse) => {
        const items = response.data.map((item: string, index: number) => ({
          id: index,
          name: item,
        }));
        this.setFiltersOptions({
          ...this.filtersOptions,
          epd_data_sources: items,
        });
      })
      .catch((err) => console.error(err, "ProductEditorGetEpdDatasetSources"));
  }

  private groupByListOfObjects(
    itemsList: CPXValueOUT[],
    key: keyof CPXValueOUT
  ) {
    return itemsList.reduce(function (
      result: { [key: string]: CPXValueOUT[] },
      x: CPXValueOUT
    ) {
      if (x[key]) {
        (result[x[key] as string] = result[x[key] as string] || []).push(x);
      }
      return result;
    },
    {});
  }

  private replaceNameWithQualification(cpxGroup: CPXValueOUT[]) {
    return cpxGroup
      .map((item) => {
        item.name = item.qualification ?? null;
        return item;
      })
      .sort((a, b) => (a.name as string).localeCompare(b.name as string));
  }

  async fetchCPXValues() {
    if (this.optionsLists.preuse_qualification.length) return;
    await productEditorApi()
      .inventoryApiV1RoutersProductEditorGetCpxValues()
      .then((response: AxiosResponse) => {
        const cpxGroups = this.groupByListOfObjects(response.data, "category");
        this.setOptionsLists({
          ...this.optionsLists,
          preuse_qualification: this.replaceNameWithQualification(
            cpxGroups["preuse"]
          ),
          separability_qualification: this.replaceNameWithQualification(
            cpxGroups["separability"]
          ),
          deconstructability_qualification: this.replaceNameWithQualification(
            cpxGroups["deconstructability"]
          ),
          reusability_qualification: this.replaceNameWithQualification(
            cpxGroups["reusability"]
          ),
        });
      })
      .catch((err) => console.error(err, "ProductEditorGetCpxValues"));
  }

  async fetchMaterialCategories() {
    await productEditorApi()
      .inventoryApiV1RoutersProductEditorGetCategories("material_general")
      .then((response) => {
        this.setFiltersOptions({
          ...this.filtersOptions,
          material_categories: response.data,
        });
      })
      .catch((err) => console.error(err, "ProductEditorGetCategories"));
  }

  private getCPXValue(key: keyof CustomProduct, value?: string) {
    const selectedID = value || String(this.data[key]);
    return this.optionsLists[key].find(
      (item) => item.id === selectedID
    ) as CPXValueOUT;
  }

  getRelevantScore(key: keyof CustomProduct, value?: string) {
    return Number(this.getCPXValue(key, value)?.numeric_value);
  }

  getRelevantQualification(key: keyof CustomProduct, value?: string) {
    return String(this.getCPXValue(key, value)?.name);
  }

  getRelevantCPXID(key: keyof CustomProduct, value?: string) {
    return String(this.getCPXValue(key, value)?.id);
  }

  checkRowsValidity() {
    const hasError =
      this.checkEpdValidationErrors() ||
      this.checkFactorValidationErrors() ||
      this.checkMaterialValidationErrors();
    return hasError;
  }

  private getEPDGroups(): ProductCreationEPDGroup[] {
    return this.data.groups.map((group) => ({
      epds: group.epds.map((item) => ({
        material: String(item.material?.id),
        factor: Number(item?.factor),
        epd: String(item?.epd?.id),
      })),
      generate_graphic: group.generate_graphic,
    }));
  }

  getDefaultEPDDatabaseName(groupIndex: number) {
    return this.data.groups[groupIndex].epds?.find(
      (item) => item?.epd?.database
    )?.epd?.database;
  }

  setFiltersOptions(filtersOptions: FilterOptions) {
    this.filtersOptions = filtersOptions;
  }

  async getDinCategoriesLevel1() {
    const authHeader = getLocalAuthHeader();
    return await searchAPI
      .inventoryApiV1RoutersSearchEngineProductDinCategories(
        "level_1",
        authHeader
      )
      .then((response) => {
        this.setOptionsLists({
          ...this.optionsLists,
          din_category: response.data?.map((item) => ({
            id: item.id,
            name: `${item.category_number} ${item.name}`,
            value: item.name,
          })),
        });
      })
      .catch((error) =>
        console.error("SearchEngineProductDinCategories.error", error)
      );
  }

  async registerCustomProduct(): Promise<CustomBuildingProductOUT | void> {
    const props: CustomBuildingProductIN = this.prepareData();
    return await productEditorApi()
      .inventoryApiV1RoutersProductEditorRegisterCustomBuildingProduct(props)
      .then((response) => {
        if (response.data) return response.data;
      });
  }

  private prepareData(): CustomBuildingProductIN {
    const orgProperties = this.data.properties.map((item) => {
      return {
        [item.slug]: {
          name: item.name,
          property_type: item.property_type,
          value:
            item.property_type === "number"
              ? item.value
                ? Number(item.value)
                : null
              : item.value ?? "",
        },
      };
    });

    return {
      name: this.data.name,
      din_category: this.data.din_category,
      life_span: Number(this.data.life_span),
      automapping_tags: this.data.auto_mapping_tags.map((item) => item.name),
      cpx_values: {
        preuse_id: this.getRelevantCPXID("preuse_qualification"),
        separability_id: this.getRelevantCPXID("separability_qualification"),
        deconstructability_id: this.getRelevantCPXID(
          "deconstructability_qualification"
        ),
        reusability_id: this.getRelevantCPXID("reusability_qualification"),
      },
      organisation_id: String(sidebarMenuStore.currentOrganisation?.id),
      epd_groups: this.getEPDGroups(),
      building: searchStore.usingOrganisationSearch
        ? null
        : (buildingStore.buildingID as string),
      reference_unit: this.data.reference_unit
        ?.id as unknown as CustomBuildingProductReferenceUnitEnum,
      file_ids: [
        ...this.data.images.map((item) => item.id),
        ...this.uploadedImagesIDs,
        ...this.data.files.map((item) => item.file_id),
        ...this.uploadedFilesIDs,
        ...this.uploadedDiagramIDs, // we always replace the diagram for the new one
      ],
      org_properties: Object.assign({}, ...orgProperties),
    };
  }

  private async removeFiles() {
    if (this.imagesToRemove.length > 0) {
      await Promise.all(
        this.imagesToRemove.map(async (imageID) => {
          await this.deleteFile(imageID);
        })
      );
    }
    const filteredImages = this.data.images.filter(
      (item) => !this.imagesToRemove.includes(item.id)
    );
    this.onChangeImages(filteredImages);

    if (this.filesToRemove.length > 0) {
      await Promise.all(
        this.filesToRemove.map(async (fileID) => {
          await this.deleteFile(fileID);
        })
      );
    }
    const filteredFiles = this.data.files.filter(
      (item) => !this.filesToRemove.includes(item.file_id)
    );
    this.onChangeFiles(filteredFiles);
  }

  async updateCustomProduct() {
    await this.removeFiles();

    const props: CustomBuildingProductIN = this.prepareData();
    return await productEditorApi().inventoryApiV1RoutersProductEditorEditCustomBuildingProduct(
      this.data.id,
      props
    );
  }

  async deleteProduct(product_id: string) {
    return await productEditorApi().inventoryApiV1RoutersProductEditorDeleteCustomBuildingProduct(
      product_id
    );
  }

  async submitCustomEPDRequest(epd_link: string) {
    const orgID = sidebarMenuStore.currentOrganisation?.id;
    if (!orgID || !epd_link) return;
    return await inventoryEPDsApi().inventoryApiV1RoutersEpdSubmitCustomEpdCreationRequest(
      {
        epd_link: epd_link,
        organisation_id: orgID,
      }
    );
  }

  async retrieveFullProductDetails(product_id: string) {
    if (!product_id) return;
    const authHeader = getLocalAuthHeader();
    return await inventoryProductsApi
      .inventoryApiV1RoutersProductsGetProductSearchDetails(
        product_id,
        authHeader
      )
      .then(async (response) => {
        return response.data;
      });
  }

  async uploadFilesInfo(filesList: FileUploadSchema[]) {
    const orgID = sidebarMenuStore.currentOrganisation?.id;
    if (!orgID) return;
    return await fileUploadApi()
      .uploadApiV1RoutersFileUploadsUploadFiles(orgID, {
        files: filesList,
      })
      .then((response) => response.data)
      .catch((err) => console.error("uploadFile", err));
  }

  async deleteFile(fileID: string) {
    const orgID = sidebarMenuStore.currentOrganisation?.id;
    if (!orgID) return;
    return await fileUploadApi()
      .uploadApiV1RoutersFileUploadsDeleteUserFile(orgID, fileID)
      .then((response) => response.data)
      .catch((err) => console.error("deleteFile", err));
  }

  async getAllGroupsInsights() {
    await Promise.all(
      this.data.groups.map((_, index) => this.getEPDGroupInsights(index))
    );
  }

  updatePercentage = action(
    (
      groupIndex: number,
      epdIndex: number,
      value: number | null | undefined
    ) => {
      this.data.groups[groupIndex].epds[epdIndex].percentage = value;
    }
  );

  async getEPDGroupInsights(groupIndex: number) {
    this.setInsightsLoading(true);

    if (!this.data.reference_unit) {
      this.setGraphicData(null);
      this.setInsightsLoading(false);
      return;
    }
    const currentGroup = this.data.groups[groupIndex];
    const layers = currentGroup.epds
      .map((rec) => {
        if (!rec.epd || !rec.material || !rec.factor) return;
        return {
          epd_id: rec.epd?.id,
          material_id: rec.material?.id,
          conversion_factor: Number(rec.factor),
        };
      })
      .filter(Boolean);

    const dataComplete = layers.length === currentGroup.epds.length;
    if (!dataComplete && currentGroup.generate_graphic) {
      this.setGraphicData(null);
      this.setInsightsLoading(false);
      return;
    }

    const props: EPDGroupInsightsIN = {
      product_reference_unit: this.data.reference_unit
        .id as ReferenceUnitChoices,
      layers: layers as [],
    };
    await epdInsightsApi()
      .buildingProductsApiV1RoutersEpdInsightsGetEpdGroupInsights(props)
      .then((response) => {
        const data = response.data;
        if (currentGroup.generate_graphic && data.graphic) {
          this.setGraphicData(data.graphic);
        }
        runInAction(() => {
          currentGroup.epds.forEach(
            (rec, index) =>
              (rec.percentage = data.epd_group?.[index]?.calculated_ratio)
          );
        });
      })
      .catch((err) => {
        currentGroup.generate_graphic && this.setGraphicData(null);
        console.error("getEPDsInsights", err);
      })
      .finally(() => this.setInsightsLoading(false));
  }

  async loadPatterns() {
    const result: PatternDictionary = {};
    await Promise.all(
      Object.keys(all_svgs).map((pattern_key) => {
        return new Promise<string>((resolve) => {
          const img = new Image();
          img.src = all_svgs[pattern_key];
          img.onload = () => {
            result[pattern_key] = img;
            resolve(pattern_key);
          };
        });
      })
    );

    this.setPatternImages(result);
  }

  async getPrefilledProductData(
    epdId: string,
    is2050: boolean,
    objectIDs: string[]
  ) {
    const orgId = sidebarMenuStore.currentOrganisation?.id;
    if (!orgId) return;
    return await buildingProductEditorApi()
      .buildingProductsApiV1RoutersProductEditorGetPrefilledProductData({
        organisation_id: orgId,
        epd_ids: is2050 ? [] : [epdId],
        materials2050_epd_ids: is2050 ? [epdId] : [],
        object_ids: objectIDs,
      })
      .then((response) => response.data)
      .catch((error) => console.error("getPrefilledProductData", error));
  }

  constructor() {
    makeAutoObservable(this);
  }

  static instance: BuildingProductStore;
  static getInstance(): BuildingProductStore {
    if (!BuildingProductStore.instance) {
      BuildingProductStore.instance = new BuildingProductStore();
    }
    return BuildingProductStore.instance;
  }
}

export const BPEStore = BuildingProductStore.getInstance();
